<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">
      <!-- Modal Header -->
      <div class="px-6 py-5 bg-gradient-to-r from-amber-600 to-amber-700 text-white flex justify-between items-center">
        <div>
          <h3 class="text-xl font-bold flex items-center gap-2">
            <span>⛽</span> Dynamic Fuel Pricing
          </h3>
          <p class="text-xs text-amber-100 mt-1">Manage market fuel rates for automated usage costing and equipment logs</p>
        </div>
        <button 
          @click="closeModal" 
          class="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto space-y-6 flex-1">
        <!-- New Price Form -->
        <div class="bg-amber-50/70 p-4 rounded-xl border border-amber-200/60">
          <h4 class="text-sm font-bold text-amber-900 mb-3 flex items-center gap-1.5">
            <span>➕</span> {{ editingId ? 'Edit Fuel Price' : 'Set New Market Fuel Price' }}
          </h4>
          <form @submit.prevent="savePrice" class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Fuel Type</label>
              <select 
                v-model="form.fuel_type" 
                required 
                class="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
              >
                <option value="Diesel">Diesel</option>
                <option value="Petrol">Petrol</option>
                <option value="Kerosene">Kerosene</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Price / Liter (TZS)</label>
              <input 
                v-model.number="form.price_per_liter" 
                type="number" 
                step="0.01" 
                min="1" 
                required 
                placeholder="e.g. 2950" 
                class="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none font-semibold"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Effective Date</label>
              <input 
                v-model="form.effective_date" 
                type="date" 
                required 
                class="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
              />
            </div>

            <div class="sm:col-span-2">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Notes / Market Reason (Optional)</label>
              <input 
                v-model="form.notes" 
                type="text" 
                placeholder="e.g. EWURA Monthly pump price revision" 
                class="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
              />
            </div>

            <div class="flex items-end gap-2">
              <button 
                type="submit" 
                :disabled="loading"
                class="w-full py-2 px-4 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-lg shadow transition-all disabled:opacity-50"
              >
                {{ loading ? 'Saving...' : (editingId ? 'Update' : 'Save Price') }}
              </button>
              <button 
                v-if="editingId" 
                type="button" 
                @click="resetForm" 
                class="py-2 px-3 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        <!-- Prices History List -->
        <div>
          <div class="flex justify-between items-center mb-3">
            <h4 class="text-sm font-bold text-gray-800">Current & Historic Fuel Rates</h4>
            <button @click="loadPrices" class="text-xs text-amber-600 hover:underline font-semibold flex items-center gap-1">
              <span>🔄</span> Refresh
            </button>
          </div>

          <div v-if="loading && prices.length === 0" class="py-8 text-center text-sm text-gray-400">
            Loading fuel prices...
          </div>

          <div v-else-if="prices.length === 0" class="py-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <p class="text-sm text-gray-500">No fuel prices registered yet.</p>
            <p class="text-xs text-gray-400 mt-1">Set the current market prices above so equipment usage can calculate costs automatically.</p>
          </div>

          <div v-else class="overflow-x-auto border border-gray-200 rounded-xl">
            <table class="min-w-full divide-y divide-gray-200 text-sm text-left">
              <thead class="bg-gray-50 text-xs font-semibold text-gray-600 uppercase">
                <tr>
                  <th class="px-4 py-3">Fuel Type</th>
                  <th class="px-4 py-3">Price / Liter</th>
                  <th class="px-4 py-3">Effective Date</th>
                  <th class="px-4 py-3">Status</th>
                  <th class="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr v-for="item in prices" :key="item.id" class="hover:bg-amber-50/30 transition-colors">
                  <td class="px-4 py-3 font-semibold text-gray-900 flex items-center gap-2">
                    <span class="w-2 h-2 rounded-full" :class="item.fuel_type === 'Diesel' ? 'bg-amber-500' : 'bg-blue-500'"></span>
                    {{ item.fuel_type }}
                  </td>
                  <td class="px-4 py-3 font-bold text-gray-900">
                    TZS {{ Number(item.price_per_liter).toLocaleString() }}
                  </td>
                  <td class="px-4 py-3 text-gray-600">
                    {{ item.effective_date }}
                  </td>
                  <td class="px-4 py-3">
                    <span 
                      v-if="item.is_active" 
                      class="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700"
                    >
                      Active
                    </span>
                    <span 
                      v-else 
                      class="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-500"
                    >
                      Inactive
                    </span>
                  </td>
                  <td class="px-4 py-3 text-right space-x-2">
                    <button 
                      @click="editPrice(item)" 
                      class="text-xs font-medium text-amber-600 hover:text-amber-800"
                    >
                      Edit
                    </button>
                    <button 
                      @click="deletePrice(item.id)" 
                      class="text-xs font-medium text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
        <button 
          @click="closeModal" 
          class="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg text-sm transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { fuelPriceApi, type FuelPrice, type FuelPriceCreate } from '@/api/fuelPriceApi'
import { useNotificationStore } from '@/stores/notification'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'updated'): void
}>()

const notificationStore = useNotificationStore()
const loading = ref(false)
const prices = ref<FuelPrice[]>([])
const editingId = ref<number | null>(null)

const getTodayDate = () => new Date().toISOString().split('T')[0]

const form = ref<FuelPriceCreate>({
  fuel_type: 'Diesel',
  price_per_liter: 2900,
  effective_date: getTodayDate(),
  is_active: true,
  notes: ''
})

const loadPrices = async () => {
  try {
    loading.value = true
    prices.value = await fuelPriceApi.getAll()
  } catch (err: any) {
    console.error('Failed to load fuel prices', err)
  } finally {
    loading.value = false
  }
}

const savePrice = async () => {
  try {
    loading.value = true
    if (editingId.value) {
      await fuelPriceApi.update(editingId.value, form.value)
      notificationStore.showSuccess('Fuel price updated successfully!')
    } else {
      await fuelPriceApi.create(form.value)
      notificationStore.showSuccess('New market fuel price saved!')
    }
    resetForm()
    await loadPrices()
    emit('updated')
  } catch (err: any) {
    const msg = err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save fuel price'
    notificationStore.showError(msg)
  } finally {
    loading.value = false
  }
}

const editPrice = (item: FuelPrice) => {
  editingId.value = item.id
  form.value = {
    fuel_type: item.fuel_type,
    price_per_liter: Number(item.price_per_liter),
    effective_date: item.effective_date,
    is_active: item.is_active,
    notes: item.notes || ''
  }
}

const deletePrice = async (id: number) => {
  if (!confirm('Are you sure you want to delete this fuel price record?')) return
  try {
    loading.value = true
    await fuelPriceApi.delete(id)
    notificationStore.showSuccess('Fuel price removed.')
    await loadPrices()
    emit('updated')
  } catch (err: any) {
    notificationStore.showError('Failed to delete fuel price')
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    fuel_type: 'Diesel',
    price_per_liter: 2900,
    effective_date: getTodayDate(),
    is_active: true,
    notes: ''
  }
}

const closeModal = () => {
  resetForm()
  emit('close')
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      loadPrices()
    }
  },
  { immediate: true }
)
</script>
